<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { VueCookieNext } from 'vue-cookie-next';
import Menu from 'primevue/menu';
import { PersonStores, useAuthStore, useCalendarStore, useTenantStore } from '@/store';
import UserCreate from '@/views/Users/components/UserCreate/UserCreate.vue';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';
import router from '@/router';

const menu = ref<Menu>();
const displayEditProfileModal = ref(false);

const authStore = useAuthStore();
const calendarStore = useCalendarStore();
const tenantStore = useTenantStore();
const profileStore = PersonStores.useUserProfileStore();

const isAdmin = computed<boolean>(() => can([PERMISSIONS.VIEW_ADMINISTRATION]));

const authItems: Record<string, string | (() => void)>[] = [
  {
    label: 'Profile',
    icon: 'm-icon person',
    command: () => handleProfile(),
  },
  {
    label: 'Sign Out',
    icon: 'm-icon logout',
    command: () => logout(),
  },
];

const unAuthItems: Record<string, string>[] = [
  {
    label: 'Sign In',
    icon: 'm-icon login',
    url: '/account/login',
  },
];

const publicItems: Record<string, string>[] = [
  {
    label: 'Privacy Policy',
    url: '/legal/privacy-policy',
    class: 'sub-item sub-item-border',
  },
  {
    label: 'Terms of Service',
    url: '/legal/terms-of-service',
    class: 'sub-item',
  },
];

const items = computed(() => [
  {
    label: authStore.loggedIn ? displayName.value : 'My account',
    items: authStore.loggedIn ? authItems : unAuthItems,
  },
  ...publicItems,
]);

watch(
  () => authStore.loggedIn,
  () => {
    if (!authStore.loggedIn) {
      calendarStore.resetEvents();
    }
  },
);

const toggleUserMenu = (toggle: Event) => menu.value?.toggle(toggle);

const firstName = computed(() => authStore.user?.first_name);

const lastName = computed(() => authStore.user?.last_name);

const initials = computed(() => `${firstName.value?.at(0)}${lastName.value?.at(0)}`.toUpperCase());

const displayName = computed(() => `${firstName.value} ${lastName.value}`);

const logout = async () => {
  const access_token_tenant = VueCookieNext.getCookie('access_token_tenant');

  if (authStore.isImpersonating) await authStore.leaveImpersonate(true);

  if (access_token_tenant) {
    const tenant = access_token_tenant.guest_tenant;
    tenantStore.leaveTenant(tenant.id);
  }

  await authStore
    .logout()
    .then(() => {
      authStore.resetAuthStore();
      tenantStore.resetTenantStore();
    })
    .catch((er) => {
      console.error(er);
    });
};

const handleProfile = async () => {
  if (!isAdmin.value) {
    await router.push('/profile');
  }
  await profileStore.getAuthUserProfile();
  displayEditProfileModal.value = true;
};

const onEditProfileToggle = () => {
  displayEditProfileModal.value = false;
};
</script>

<template>
  <div class="user-profile-container" @click="toggleUserMenu">
    <span class="user-button">
      <span v-if="authStore.loggedIn" class="text-white" v-text="initials" />
      <Icon v-else name="account_circle" class="text-white" />
    </span>
    <Menu ref="menu" :model="items" :popup="true" />
  </div>
  <div v-if="authStore.loggedIn && isAdmin">
    <UserCreate
      v-if="displayEditProfileModal"
      :id="profileStore.id"
      :person-type-acronym="authStore.acronymType"
      :is-edit="true"
      :is-admin="true"
      :header="`Edit Profile`"
      :save-button-text="`Save`"
      :is-profile="true"
      @cancel="onEditProfileToggle"
      @edit="onEditProfileToggle"
    />
  </div>
</template>

<style scoped lang="scss">
.user-profile-container {
  width: 30px;
  height: 60px;
  display: flex;
  align-items: center;
}

.user-button {
  display: inline-flex;
  position: relative;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background-color: var(--primary-color-medium);
  border: none;
  border-radius: 50%;
  transition: 0.2s ease;

  &:hover {
    opacity: 0.75;
  }
}
</style>
